import { lazy } from 'react';
import { createBrowserRouter, Navigate, RouteObject } from 'react-router-dom';
import WrapperRouteComponent from './config';
import Middleware from '@/middleware';
import { PAGE_TITLES } from '@/constants/titles';
import BaseLayout from '@/layouts/base.layout';
import MainLayout from '@/layouts/main.layout';
import AuthLayout from '@/layouts/auth.layout';
import { AuthWrapper } from '@/components/HOC/AuthWrapper';
import { lazyRetry } from '@/utils/lazy-retry';
import { SmartRedirect } from '@/components/HOC/SmartRedirect';

// Lazy load các components
//module
const StoresPage = lazyRetry(() => import(/* webpackChunkName: "stores"*/ '@/modules/stores'));
const StoreDetailPage = lazyRetry(
  () => import(/* webpackChunkName: "store-detail"*/ '@/modules/stores/detail'),
);

const OrderPage = lazy(() => import(/* webpackChunkName: "order"*/ '@/modules/order'));
const OrderDetailPage = lazy(
  () => import(/* webpackChunkName: "order-detail"*/ '@/modules/order/OrderDetail.page'),
);

const RolesPage = lazy(() => import(/* webpackChunkName: "roles"*/ '@/modules/roles'));

const DepartmentPage = lazy(
  () => import(/* webpackChunkName: "department"*/ '@/modules/department'),
);
const SheetPage = lazy(() => import(/* webpackChunkName: "sheet"*/ '@/modules/sheet'));
const SheetDetailPage = lazy(
  () => import(/* webpackChunkName: "sheet-detail"*/ '@/modules/sheet/detail'),
);

const PermissionPage = lazy(
  () => import(/* webpackChunkName: "permission"*/ '@/modules/permission'),
);
const PermissionDetailPage = lazy(
  () => import(/* webpackChunkName: "permission-detail"*/ '@/modules/permission/detail'),
);
const DepartmentDetailPage = lazyRetry(
  () => import(/* webpackChunkName: "department-detail"*/ '@/modules/department/detail'),
);

const FunctionsPage = lazy(() => import(/* webpackChunkName: "functions"*/ '@/modules/functions'));
const FunctionDetailPage = lazy(
  () => import(/* webpackChunkName: "functions-detail"*/ '@/modules/functions/detail'),
);

const UserPage = lazy(() => import(/* webpackChunkName: "user"*/ '@/modules/user'));
const UserPageDetailPage = lazy(
  () => import(/* webpackChunkName: "user-detail"*/ '@/modules/user/detail'),
);
const AddUserWithRolePage = lazy(
  () => import(/* webpackChunkName: "add-user-with-role"*/ '@/modules/user/add-user-with-role'),
);

const FactoryDashboardPage = lazy(
  () => import(/* webpackChunkName: "factory-dashboard"*/ '@/modules/factory-dashboard'),
);
const FulfillmentPage = lazy(
  () => import(/* webpackChunkName: "fulfillment"*/ '@/modules/fulfillment'),
);
const FulfillmentDetailPage = lazy(
  () => import(/* webpackChunkName: "fulfillment-detail"*/ '@/modules/fulfillment/detail'),
);

const ActivityLogPage = lazy(
  () => import(/* webpackChunkName: "activity-log"*/ '@/modules/activity-log'),
);

const FactoriesPage = lazy(() => import(/* webpackChunkName: "factories"*/ '@/modules/factories'));

const FactoriesDetailPage = lazyRetry(
  () => import(/* webpackChunkName: "factories-detail"*/ '@/modules/factories/detail'),
);

const SettingPage = lazy(() => import(/* webpackChunkName: "settings"*/ '@/modules/settings'));
const TeamsPage = lazy(() => import(/* webpackChunkName: "Team"*/ '@/modules/teams'));

const TeamsDetailPage = lazy(
  () => import(/* webpackChunkName: "team-detail"*/ '@/modules/teams/team-detail'),
);
//Auth
const LoginPage = lazy(() => import(/* webpackChunkName: "login"*/ '@/pages/Auth/Login'));

//not-found
const NotFoundPage = lazy(() => import('@/pages/NotFound'));

// Định nghĩa các routes
const routes: RouteObject[] = [
  {
    path: '/',
    element: (
      <AuthWrapper>
        <Middleware mode="private">
          <WrapperRouteComponent title="">
            <MainLayout />
          </WrapperRouteComponent>
        </Middleware>
      </AuthWrapper>
    ),
    children: [
      {
        index: true,
        element: <SmartRedirect />,
      },
      {
        path: '/stores',
        element: (
          <WrapperRouteComponent title={PAGE_TITLES.STORES}>
            <StoresPage />
          </WrapperRouteComponent>
        ),
      },
      {
        path: '/stores/:id',
        element: (
          <WrapperRouteComponent title={PAGE_TITLES.STORES}>
            <StoreDetailPage />
          </WrapperRouteComponent>
        ),
      },
      {
        path: '/orders',
        element: (
          <WrapperRouteComponent title={PAGE_TITLES.ORDERS}>
            <OrderPage />
          </WrapperRouteComponent>
        ),
      },
      {
        path: '/orders/:id',
        element: (
          <WrapperRouteComponent title={PAGE_TITLES.ORDERS}>
            <OrderDetailPage />
          </WrapperRouteComponent>
        ),
      },
      {
        path: '/roles',
        element: (
          <WrapperRouteComponent title={PAGE_TITLES.ROLES}>
            <RolesPage />
          </WrapperRouteComponent>
        ),
      },
      {
        path: '/departments',
        element: (
          <WrapperRouteComponent title={PAGE_TITLES.DEPARTMENTS}>
            <DepartmentPage />
          </WrapperRouteComponent>
        ),
      },
      {
        path: '/departments/:departmentId',
        element: (
          <WrapperRouteComponent title={PAGE_TITLES.DEPARTMENTS}>
            <DepartmentDetailPage />
          </WrapperRouteComponent>
        ),
      },
      {
        path: '/functions',
        element: (
          <WrapperRouteComponent title={PAGE_TITLES.FUNCTIONS}>
            <FunctionsPage />
          </WrapperRouteComponent>
        ),
      },
      {
        path: '/functions/:id',
        element: (
          <WrapperRouteComponent title={PAGE_TITLES.FUNCTIONS}>
            <FunctionDetailPage />
          </WrapperRouteComponent>
        ),
      },
      {
        path: '/permissions',
        element: (
          <WrapperRouteComponent title={PAGE_TITLES.PERMISSION}>
            <PermissionPage />
          </WrapperRouteComponent>
        ),
      },
      {
        path: '/permissions/:id',
        element: (
          <WrapperRouteComponent title={PAGE_TITLES.PERMISSION}>
            <PermissionDetailPage />
          </WrapperRouteComponent>
        ),
      },
      {
        path: '/users',
        element: (
          <WrapperRouteComponent title={PAGE_TITLES.USER}>
            <UserPage />
          </WrapperRouteComponent>
        ),
      },
      {
        path: '/users/create-user',
        element: (
          <WrapperRouteComponent title={PAGE_TITLES.USER}>
            <AddUserWithRolePage />
          </WrapperRouteComponent>
        ),
      },
      {
        path: '/users/:id',
        element: (
          <WrapperRouteComponent title={PAGE_TITLES.USER}>
            <UserPageDetailPage />
          </WrapperRouteComponent>
        ),
      },
      {
        path: '/sheets',
        element: (
          <WrapperRouteComponent title={PAGE_TITLES.SHEET}>
            <SheetPage />
          </WrapperRouteComponent>
        ),
      },
      {
        path: '/sheets/:sheetId',
        element: (
          <WrapperRouteComponent title={PAGE_TITLES.SHEET}>
            <SheetDetailPage />
          </WrapperRouteComponent>
        ),
      },
      {
        path: '/dashboard',
        element: (
          <WrapperRouteComponent title={PAGE_TITLES.SHEET}>
            <FactoryDashboardPage />
          </WrapperRouteComponent>
        ),
      },
      {
        path: '/fulfillments',
        element: (
          <WrapperRouteComponent title={PAGE_TITLES.FULFILLMENT}>
            <FulfillmentPage />
          </WrapperRouteComponent>
        ),
      },
      {
        path: '/fulfillments/:fulfillmentId',
        element: (
          <WrapperRouteComponent title={PAGE_TITLES.FULFILLMENT}>
            <FulfillmentDetailPage />
          </WrapperRouteComponent>
        ),
      },
      {
        path: '/activity-logs',
        element: (
          <WrapperRouteComponent title={PAGE_TITLES.ACTIVITY_LOG}>
            <ActivityLogPage />
          </WrapperRouteComponent>
        ),
      },
      {
        path: '/teams',
        element: (
          <WrapperRouteComponent title={PAGE_TITLES.TEAMS}>
            <TeamsPage />
          </WrapperRouteComponent>
        ),
      },

      {
        path: '/teams/:teamId',
        element: (
          <WrapperRouteComponent title={PAGE_TITLES.TEAMS}>
            <TeamsDetailPage />
          </WrapperRouteComponent>
        ),
      },

      {
        path: '/factories',
        element: (
          <WrapperRouteComponent title={PAGE_TITLES.FACTORIES}>
            <FactoriesPage />
          </WrapperRouteComponent>
        ),
      },

      {
        path: '/factories/:factoryId',
        element: (
          <WrapperRouteComponent title={PAGE_TITLES.FACTORIES}>
            <FactoriesDetailPage />
          </WrapperRouteComponent>
        ),
      },

      {
        path: '/settings',
        element: (
          <WrapperRouteComponent title={PAGE_TITLES.SETTINGS}>
            <SettingPage />
          </WrapperRouteComponent>
        ),
      },
    ],
  },
  {
    path: '/',
    element: (
      <WrapperRouteComponent title="">
        <Middleware mode="non-login">
          <AuthLayout />
        </Middleware>
      </WrapperRouteComponent>
    ),
    children: [
      {
        path: 'login',
        element: (
          <WrapperRouteComponent title={PAGE_TITLES.LOGIN}>
            <LoginPage />
          </WrapperRouteComponent>
        ),
      },
    ],
  },
  {
    path: '',
    element: (
      <AuthWrapper>
        <WrapperRouteComponent title="">
          <Middleware mode="private">
            <BaseLayout />
          </Middleware>
        </WrapperRouteComponent>
      </AuthWrapper>
    ),
    children: [],
  },
  {
    path: '/not-found',
    element: (
      <WrapperRouteComponent title={PAGE_TITLES.NOT_FOUND}>
        <NotFoundPage />
      </WrapperRouteComponent>
    ),
  },
  {
    path: '*',
    element: <Navigate to="/not-found" replace />,
  },
];

// Export router
export const router = createBrowserRouter(routes);
